import { useEffect, useState } from "react";
import axios from "axios";
import { X, Copy, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { API, useAuth } from "@/lib/auth";
import { track } from "@/lib/analytics";

const STORAGE_KEY = "mdb_welcome_popup_v1";
const DELAY_MS = 8000; // show after 8s of engagement

// Routes where the popup would feel intrusive
const BLOCKED_PATHS = ["/cart", "/payment/", "/admin", "/account", "/auth/", "/legal/", "/quiz", "/bridal"];

export default function WelcomePopup() {
  const { user } = useAuth() || {};
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [claimed, setClaimed] = useState(null); // {code, percent_off}
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (user) return; // signed-in customers already know us
    if (open) return; // already open
    try {
      if (localStorage.getItem(STORAGE_KEY)) return; // already seen or dismissed
    } catch (err) {
      console.warn("welcome-popup: localStorage unavailable", err);
    }
    if (BLOCKED_PATHS.some((p) => window.location.pathname.startsWith(p))) return;

    const show = () => {
      setOpen(true);
      try { track("welcome_popup_shown", {}); } catch (err) { console.warn("track failed", err); }
    };

    // Trigger 1: after DELAY_MS
    const t = setTimeout(show, DELAY_MS);

    // Trigger 2: exit intent on desktop (mouse leaves near top of viewport)
    const onLeave = (e) => {
      if (e.clientY <= 0) show();
    };
    document.addEventListener("mouseleave", onLeave);

    return () => {
      clearTimeout(t);
      document.removeEventListener("mouseleave", onLeave);
    };
  }, [user, open]);

  // Prevent body scroll when open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const persistDismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ ts: new Date().toISOString() })); } catch (err) { console.warn("welcome-popup: localStorage set failed", err); }
  };

  const close = () => {
    persistDismiss();
    setOpen(false);
    try { track("welcome_popup_dismissed", { claimed: !!claimed }); } catch (err) { console.warn("track failed", err); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/welcome-offer/claim`, { email: email.trim() });
      setClaimed({ code: r.data?.code, percent_off: r.data?.percent_off });
      try { track("welcome_popup_claimed", { percent_off: r.data?.percent_off }); } catch (err) { console.warn("track failed", err); }
      persistDismiss();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not claim your code — try again shortly");
    } finally {
      setBusy(false);
    }
  };

  const copyCode = async () => {
    if (!claimed?.code) return;
    try {
      await navigator.clipboard.writeText(claimed.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.success(`Code: ${claimed.code}`);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-popup-title"
      data-testid="welcome-popup"
      className="fixed inset-0 z-[70] flex items-center justify-center px-4 sm:px-6"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[hsl(var(--onyx))]/60 backdrop-blur-sm animate-in fade-in duration-300"
        onClick={close}
        data-testid="welcome-popup-backdrop"
      />

      {/* Card */}
      <div className="relative w-full max-w-md sm:max-w-lg bg-[hsl(var(--cream))] rounded-[2rem] shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-300">
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          data-testid="welcome-popup-close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/5 hover:bg-black/10 flex items-center justify-center transition-colors"
        >
          <X size={16} />
        </button>

        {/* Blush header */}
        <div className="relative bg-blush px-8 pt-10 pb-8 text-center">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--gold))]">
            <Sparkles size={12} /> A little welcome gift
          </div>
          <h2
            id="welcome-popup-title"
            className="font-display text-3xl sm:text-4xl mt-3 leading-tight text-balance"
          >
            {claimed ? "You're in — enjoy." : "Take 10% off your first order."}
          </h2>
          {!claimed && (
            <p className="mt-3 text-sm text-[hsl(var(--onyx)/0.7)] max-w-sm mx-auto">
              Join the list for salon exclusives, new NøoksProRange drops and extension tips —
              your welcome code lands in your inbox.
            </p>
          )}
        </div>

        {/* Body */}
        <div className="px-8 py-8">
          {claimed ? (
            <div className="text-center">
              <div
                data-testid="welcome-popup-code"
                className="mt-1 rounded-2xl border border-dashed border-[hsl(var(--gold))/0.7] bg-white p-6"
              >
                <div className="overline text-[hsl(var(--gold))]">Your code</div>
                <div className="font-display text-3xl tracking-[0.15em] mt-2">{claimed.code}</div>
                <div className="text-xs text-[hsl(var(--onyx)/0.55)] mt-2">
                  {claimed.percent_off}% off — apply at checkout · shop orders only
                </div>
              </div>
              <button
                type="button"
                onClick={copyCode}
                data-testid="welcome-popup-copy"
                className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[hsl(var(--onyx))] text-[hsl(var(--cream))] text-sm font-medium hover:opacity-90 transition-opacity"
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
                {copied ? "Copied" : "Copy code"}
              </button>
              <div className="mt-3">
                <a
                  href="/shop"
                  data-testid="welcome-popup-shop-cta"
                  className="text-sm underline underline-offset-4 text-[hsl(var(--onyx)/0.7)] hover:text-[hsl(var(--onyx))]"
                  onClick={close}
                >
                  Start shopping →
                </a>
              </div>
              <p className="text-[11px] text-[hsl(var(--onyx)/0.5)] mt-6 leading-relaxed">
                We've emailed your code too. Excludes gift vouchers. One redemption per customer.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="welcome-popup-form">
              <label className="block">
                <span className="overline">Email</span>
                <input
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  data-testid="welcome-popup-email"
                  className="mt-2 w-full h-12 rounded-full bg-white border border-black/10 px-5 text-sm outline-none focus:border-[hsl(var(--onyx))] transition-colors"
                />
              </label>
              <button
                type="submit"
                disabled={busy}
                data-testid="welcome-popup-submit"
                className="w-full h-12 rounded-full bg-[hsl(var(--onyx))] text-[hsl(var(--cream))] text-sm font-medium disabled:opacity-60 hover:opacity-90 transition-opacity"
              >
                {busy ? "Sending your code…" : "Send me my 10% code"}
              </button>
              <button
                type="button"
                onClick={close}
                data-testid="welcome-popup-decline"
                className="w-full text-xs text-[hsl(var(--onyx)/0.55)] hover:text-[hsl(var(--onyx))] transition-colors"
              >
                No thanks — I'll pay full price
              </button>
              <p className="text-[11px] text-[hsl(var(--onyx)/0.5)] text-center leading-relaxed">
                By subscribing you agree to receive marketing emails. Unsubscribe any time.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
